import { isAxiosError } from 'axios';
import { ManagedBlockingConflictCode } from 'enums';
import { isManagedBlockedAppValid } from 'utils/managed-apps.util';
import { HTTP_STATS_CODE } from 'constants/general';
import {
  MANAGED_BLOCKED_APPS_LIMIT,
  MANAGED_BLOCKLIST_LIMIT,
  MANAGED_DAY_CODES,
  MANAGED_REASON_MAX_LENGTH,
  MANAGED_FALLBACK_TIME_ZONE,
  MANAGED_POLICY_STATUS_KEYS,
  MANAGED_PUBLISHED_WITH_DRAFT_KEY,
  NEW_MANAGED_POLICY
} from 'constants/managed-blocking';
import { ManagedPolicyStatus } from 'enums';
import {
  ManagedBlockedApp,
  ManagedBlocklistEntry,
  ManagedContext,
  ManagedDateRange,
  ManagedErrorOptions,
  ManagedException,
  ManagedNormalizedPolicy,
  ManagedParentGrant,
  ManagedPolicy,
  ManagedPolicyInput,
  ManagedWindow
} from 'interfaces/managed-blocking.interface';

const getConflictKey = (reason: unknown, conflictKey?: string) => {
  if (reason === ManagedBlockingConflictCode.SCHOOL_TRANSFER_RECOVERY) {
    return 'managed_blocking.school_transfer_recovery';
  }
  if (reason === ManagedBlockingConflictCode.PARENT_ALREADY_ACCEPTED) {
    return 'managed_blocking.parent_already_accepted';
  }
  if (reason === ManagedBlockingConflictCode.ASSIGNMENT_EXISTS) {
    return 'managed_blocking.assignment_exists';
  }
  return conflictKey ?? 'managed_blocking.conflict';
};
/**
 * `conflictKey` names what a code-less 409 means for the calling action. Only policy saves and
 * approvals conflict on a stale revision; other endpoints use 409 for other reasons.
 */
export const getManagedErrorKey = (
  error: unknown,
  { conflictKey }: ManagedErrorOptions = {}
): string => {
  if (!isAxiosError(error)) return 'managed_blocking.request_failed';
  const code = error.response?.status;
  if (
    code === HTTP_STATS_CODE.CONFLICT ||
    code === HTTP_STATS_CODE.PRECONDITION_FAILED
  ) {
    return getConflictKey(error.response?.data?.code, conflictKey);
  }
  if (
    code === HTTP_STATS_CODE.UNAUTHORIZED ||
    code === HTTP_STATS_CODE.FORBIDDEN
  ) {
    return 'managed_blocking.access_denied';
  }
  if (code === HTTP_STATS_CODE.NOT_FOUND) return 'managed_blocking.not_found';
  if (
    code === HTTP_STATS_CODE.BAD_REQUEST ||
    code === HTTP_STATS_CODE.UNPROCESSABLE_ENTITY
  ) {
    return 'managed_blocking.invalid_request';
  }
  return 'managed_blocking.request_failed';
};
export const managedPolicyInput = (
  policy: ManagedPolicyInput
): ManagedPolicyInput => ({
  name: policy.name,
  time_zone: policy.time_zone,
  is_enabled: policy.is_enabled,
  windows: policy.windows,
  blocked_categories: policy.blocked_categories,
  allowlist: policy.allowlist,
  excluded_dates: policy.excluded_dates,
  unknown_action: policy.unknown_action,
  unavailable_action: policy.unavailable_action,
  ...(policy.blocklist !== undefined && { blocklist: policy.blocklist }),
  ...(policy.blocked_apps !== undefined && {
    blocked_apps: policy.blocked_apps
  })
});

/**
 * The body to save. The two newer lists are sent when they have entries or the stored schedule
 * already has them, so saving an ordinary schedule still works against a backend without #2711.
 */
export const managedSavePayload = (
  value: ManagedPolicyInput,
  stored: ManagedPolicyInput | null
): ManagedPolicyInput => {
  const { blocklist = [], blocked_apps = [], ...rest } = value;
  return {
    ...rest,
    ...((blocklist.length > 0 || stored?.blocklist !== undefined) && {
      blocklist
    }),
    ...((blocked_apps.length > 0 || stored?.blocked_apps !== undefined) && {
      blocked_apps
    })
  };
};
export const isManagedTimeZone = (zone: string) => {
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: zone }).format();
    return zone.trim().length > 0;
  } catch {
    return false;
  }
};
const isWindowValid = (window: ManagedWindow) => {
  const isTime = (value: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
  return (
    window.days_of_week.length > 0 &&
    window.days_of_week.every((day) => MANAGED_DAY_CODES.includes(day)) &&
    isTime(window.start_time) &&
    isTime(window.end_time) &&
    (window.all_day === true || window.start_time !== window.end_time)
  );
};
export const isManagedDate = (value: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  !Number.isNaN(Date.parse(value)) &&
  new Date(value).toISOString().slice(0, 10) === value;
// A registrable host name (labels of letters, digits and hyphens, ending in a letter TLD) or IPv4.
const MANAGED_HOST_PATTERN =
  /^(?:(?:[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?\.)+[a-z][a-z\d-]{0,61}[a-z\d]|(?:\d{1,3}\.){3}\d{1,3})$/i;
export const isManagedWebsiteValid = (value: string) => {
  const trimmed = value.trim();
  if (trimmed.length === 0 || /\s/.test(trimmed)) return false;
  try {
    const url = new URL(
      trimmed.includes('://') ? trimmed : `https://${trimmed}`
    );
    return (
      ['http:', 'https:'].includes(url.protocol) &&
      MANAGED_HOST_PATTERN.test(url.hostname) &&
      url.username.length === 0 &&
      url.password.length === 0
    );
  } catch {
    return false;
  }
};
const isBlocklistEntryValid = (entry: ManagedBlocklistEntry) =>
  isManagedWebsiteValid(entry.value) &&
  (entry.reason ?? '').length <= MANAGED_REASON_MAX_LENGTH;
const validateManagedLists = ({
  blocklist = [],
  blocked_apps = []
}: ManagedPolicyInput): string => {
  if (
    blocklist.length > MANAGED_BLOCKLIST_LIMIT ||
    blocklist.some((entry) => !isBlocklistEntryValid(entry))
  ) {
    return 'managed_blocking.blocklist_invalid';
  }
  if (
    blocked_apps.length > MANAGED_BLOCKED_APPS_LIMIT ||
    blocked_apps.some((app) => !isManagedBlockedAppValid(app))
  ) {
    return 'managed_blocking.blocked_apps_invalid';
  }
  return '';
};
export const validateManagedPolicy = (policy: ManagedPolicyInput): string => {
  if (policy.name.trim().length === 0) return 'managed_blocking.name_required';
  if (!isManagedTimeZone(policy.time_zone)) {
    return 'managed_blocking.zone_invalid';
  }
  if (policy.windows.some((window) => !isWindowValid(window))) {
    return 'managed_blocking.window_invalid';
  }
  if (
    policy.excluded_dates.some(
      (range) =>
        !isManagedDate(range.start_date) ||
        !isManagedDate(range.end_date) ||
        range.end_date < range.start_date
    )
  ) {
    return 'managed_blocking.date_invalid';
  }
  if (
    policy.allowlist.some(
      (exception) =>
        !isManagedWebsiteValid(exception.url) ||
        (exception.expires_at !== undefined &&
          Number.isNaN(Date.parse(exception.expires_at)))
    )
  ) {
    return 'managed_blocking.exception_invalid';
  }
  return validateManagedLists(policy);
};
export const isManagedRequestExpired = (expiresAt: string) =>
  Number.isNaN(Date.parse(expiresAt)) || Date.parse(expiresAt) <= Date.now();
// Month names, not numeric day/month order, so the date reads the same in every locale.
export const managedDateTime = (value: string) =>
  Number.isNaN(Date.parse(value))
    ? value
    : new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short'
      }).format(new Date(value));

export const getBrowserTimeZone = () => {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isManagedTimeZone(zone) ? zone : MANAGED_FALLBACK_TIME_ZONE;
  } catch {
    return MANAGED_FALLBACK_TIME_ZONE;
  }
};

export const createNewManagedPolicy = (): ManagedPolicyInput => ({
  ...NEW_MANAGED_POLICY,
  time_zone: getBrowserTimeZone()
});

// A school schedule saved after publishing stays a draft revision while students keep using the
// published one; calling it "Draft" made admins think nothing was live.
export const managedPolicyStatusKey = (
  policy: Pick<ManagedPolicy, 'status' | 'published_revision'>
) =>
  policy.status === ManagedPolicyStatus.DRAFT &&
  policy.published_revision != null
    ? MANAGED_PUBLISHED_WITH_DRAFT_KEY
    : MANAGED_POLICY_STATUS_KEYS[policy.status];

/** The server's own validation text (Nest sends a string or a list), when it gave one. */
export const getManagedErrorDetail = (error: unknown): string => {
  if (!isAxiosError(error) || error.response?.status !== 400) return '';
  const message: unknown = error.response.data?.message;
  if (typeof message === 'string') return message;
  return Array.isArray(message)
    ? message.filter((item) => typeof item === 'string').join(' ')
    : '';
};

export const managedHistorySnapshot = (
  value: unknown
): ManagedPolicyInput | null => {
  if (value === null || typeof value !== 'object') return null;
  const candidate = value as Partial<ManagedPolicyInput>;
  if (
    typeof candidate.name !== 'string' ||
    typeof candidate.time_zone !== 'string' ||
    typeof candidate.is_enabled !== 'boolean' ||
    !Array.isArray(candidate.windows) ||
    !Array.isArray(candidate.blocked_categories) ||
    !Array.isArray(candidate.allowlist) ||
    !Array.isArray(candidate.excluded_dates) ||
    candidate.unknown_action === undefined ||
    candidate.unavailable_action === undefined
  ) {
    return null;
  }
  // Returned as stored so the summary can flag damaged entries; callers that edit must normalise.
  return candidate as ManagedPolicyInput;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const isOptionalString = (value: unknown) =>
  value == null || typeof value === 'string';

export const isManagedWindowShape = (value: unknown): value is ManagedWindow =>
  isRecord(value) &&
  Array.isArray(value.days_of_week) &&
  value.days_of_week.every((day) => typeof day === 'string') &&
  typeof value.start_time === 'string' &&
  typeof value.end_time === 'string' &&
  (value.all_day == null || typeof value.all_day === 'boolean');

const isManagedExceptionShape = (value: unknown): value is ManagedException =>
  isRecord(value) &&
  typeof value.url === 'string' &&
  typeof value.include_subdomains === 'boolean' &&
  isOptionalString(value.reason) &&
  isOptionalString(value.expires_at);

const isManagedDateRangeShape = (value: unknown): value is ManagedDateRange =>
  isRecord(value) &&
  typeof value.start_date === 'string' &&
  typeof value.end_date === 'string';

const isBlocklistShape = (value: unknown): value is ManagedBlocklistEntry =>
  isRecord(value) &&
  typeof value.value === 'string' &&
  typeof value.include_subdomains === 'boolean' &&
  isOptionalString(value.reason);
const isBlockedAppShape = (value: unknown): value is ManagedBlockedApp =>
  isRecord(value) &&
  typeof value.platform === 'string' &&
  typeof value.identifier === 'string' &&
  typeof value.name === 'string';

const isCategoryId = (value: unknown): value is string =>
  typeof value === 'string';

const keepWellFormed = <T>(
  value: unknown,
  isWellFormed: (item: unknown) => item is T
) => {
  if (!Array.isArray(value)) {
    return { items: [] as T[], malformed: value == null ? 0 : 1 };
  }
  const items = value.filter(isWellFormed);
  return { items, malformed: value.length - items.length };
};

// Optional fields come back as null from some writers; the editor and summary treat absence as undefined.
const cleanException = ({
  url,
  include_subdomains,
  reason,
  expires_at
}: ManagedException): ManagedException => ({
  url,
  include_subdomains,
  ...(reason != null && { reason }),
  ...(expires_at != null && { expires_at })
});

// Stored policies and audit snapshots have been written with malformed entries (e.g. windows saved as
// `[[]]`). Rendering must never trust their shape: drop the entries that can't be shown and count them,
// so the UI can flag the damage instead of crashing the whole page.
export const normalizeManagedPolicy = (
  policy: ManagedPolicyInput
): ManagedNormalizedPolicy => {
  const windows = keepWellFormed(policy.windows, isManagedWindowShape);
  const allowlist = keepWellFormed(policy.allowlist, isManagedExceptionShape);
  const dates = keepWellFormed(policy.excluded_dates, isManagedDateRangeShape);
  const categories = keepWellFormed(policy.blocked_categories, isCategoryId);
  const blocklist = keepWellFormed(policy.blocklist, isBlocklistShape);
  const apps = keepWellFormed(policy.blocked_apps, isBlockedAppShape);
  return {
    policy: {
      ...managedPolicyInput(policy),
      name: typeof policy.name === 'string' ? policy.name : '',
      time_zone: typeof policy.time_zone === 'string' ? policy.time_zone : '',
      windows: windows.items,
      blocked_categories: categories.items,
      allowlist: allowlist.items.map(cleanException),
      excluded_dates: dates.items,
      blocklist: blocklist.items,
      blocked_apps: apps.items
    },
    malformedCount:
      blocklist.malformed +
      apps.malformed +
      windows.malformed +
      allowlist.malformed +
      dates.malformed +
      categories.malformed
  };
};

export const managedExpiryUtcInput = (value?: string) =>
  value === undefined || Number.isNaN(Date.parse(value))
    ? ''
    : new Date(value).toISOString().slice(0, 16);

export const hasManagedBlockingContext = (
  context: ManagedContext,
  invitations: ManagedParentGrant[]
): boolean =>
  context.schools.some((school) => school.can_manage) ||
  context.children.length > 0 ||
  context.student_managed ||
  invitations.length > 0;
